/**
 * Helper to clean and format Python-style dict errors from the backend.
 * Extracts the message and presents the row key-value pairs in a readable format.
 */
export const formatImportError = (errorStr: string): string => {
    if (!errorStr) return '';

    // Check if the error string contains a python-style dictionary
    const dictStartIndex = errorStr.indexOf('{');
    const dictEndIndex = errorStr.lastIndexOf('}');

    if (dictStartIndex !== -1 && dictEndIndex !== -1 && dictEndIndex > dictStartIndex) {
        const message = errorStr.substring(0, dictStartIndex).trim();
        const dictStr = errorStr.substring(dictStartIndex, dictEndIndex + 1);

        try {
            const matches: { key: string; value: string }[] = [];
            
            // Matches: ("key"|'key')\s*:\s*("val"|'val'|unquoted_val)
            const keyValueRegex = /(?:"([^"\\]*(?:\\.[^"\\]*)*)"|'([^'\\]*(?:\\.[^'\\]*)*)')\s*:\s*(?:"([^"\\]*(?:\\.[^"\\]*)*)"|'([^'\\]*(?:\\.[^'\\]*)*)'|([^\s,{}]+))/g;
            
            let match;
            while ((match = keyValueRegex.exec(dictStr)) !== null) {
                const key = match[1] || match[2] || '';
                let val = match[3] || match[4] || match[5] || '';
                
                // Decode escaped characters
                const cleanKey = key.replace(/\\n/g, '\n').replace(/\\'/g, "'").replace(/\\"/g, '"').trim();
                let cleanVal = val.replace(/\\n/g, '\n').replace(/\\'/g, "'").replace(/\\"/g, '"').trim();
                
                const keyLower = cleanKey.toLowerCase();
                // Skip column notes or unnamed blank columns
                if (keyLower.startsWith('note:') || keyLower.startsWith('unnamed:')) {
                    continue;
                }
                
                // Format null/nan values nicely
                if (cleanVal === 'nan' || cleanVal === 'None' || cleanVal === 'null') {
                    cleanVal = 'Empty';
                }
                
                if (cleanKey) {
                    matches.push({ key: cleanKey, value: cleanVal });
                }
            }

            if (matches.length > 0) {
                const rowDetails = matches
                    .map(item => `${item.key}: "${item.value}"`)
                    .join(', ');
                
                let cleanMessage = message;
                if (cleanMessage.endsWith(':')) {
                    cleanMessage = cleanMessage.slice(0, -1).trim();
                }
                
                return `${cleanMessage} → [${rowDetails}]`;
            }
        } catch (e) {
            // Fallback
        }
    }

    return errorStr
        .replace(/:\s*nan/g, ': Empty')
        .replace(/:\s*None/g, ': Empty')
        .replace(/'/g, '"');
};
